import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';

interface BlogPost {
  title: string;
  slug: string;
  date: string;
  author: string;
  authorRole: string;
  excerpt: string;
  tags: string[];
  status: 'published' | 'scheduled' | 'draft';
}

const blogPosts: BlogPost[] = [
  {
    title: "Why Choose Professional Chauffeur Service Over Ride-Sharing Apps",
    slug: "why-choose-professional-chauffeur-service",
    date: "2024-03-20",
    author: "Michael Chen",
    authorRole: "Operations Director",
    excerpt: "Discover the key advantages of professional chauffeur services compared to ride-sharing apps, from safety and reliability to luxury and convenience.",
    tags: ["Chauffeur Service", "Luxury Transportation", "Business Travel"],
    status: "published"
  },
  {
    title: "The Ultimate Guide to Premium Airport Transportation",
    slug: "airport-transportation-guide",
    date: "2024-03-25",
    author: "David Thompson",
    authorRole: "Head of Operations",
    excerpt: "Learn how to navigate airport transportation like a pro with our comprehensive guide to premium chauffeur services, FBO pickups, and private terminal access.",
    tags: ["Airport Transportation", "FBO Service", "Private Terminal"],
    status: "published"
  },
  {
    title: "Corporate Transportation: Elevating Business Travel in 2024",
    slug: "corporate-transportation-guide",
    date: "2024-03-28",
    author: "Sarah Johnson",
    authorRole: "Corporate Services Director",
    excerpt: "Discover how premium corporate transportation services can enhance your business travel experience, from executive car service to comprehensive travel management solutions.",
    tags: ["Corporate Transportation", "Business Travel", "Executive Car Service"],
    status: "published"
  },
  {
    title: "Luxury Transportation for Special Events: A Complete Guide",
    slug: "luxury-transportation-special-events",
    date: "2024-04-05",
    author: "Emily Rodriguez",
    authorRole: "Events Coordinator",
    excerpt: "Planning a special event? Learn how premium transportation services can elevate your guests' experience and ensure seamless event logistics.",
    tags: ["Special Events", "Wedding Transportation", "VIP Service"],
    status: "scheduled"
  },
  {
    title: "The Future of Premium Transportation: Trends and Innovations",
    slug: "future-premium-transportation",
    date: "2024-04-15",
    author: "Michael Chen",
    authorRole: "Operations Director",
    excerpt: "Explore the latest trends and innovations shaping the future of premium transportation services, from sustainable vehicles to advanced booking technology.",
    tags: ["Industry Trends", "Technology", "Sustainability"],
    status: "draft"
  }
];

const BlogCalendar = () => {
  const [filter, setFilter] = useState<'all' | 'published' | 'scheduled' | 'draft'>('all');

  const filteredPosts = blogPosts.filter(post => 
    filter === 'all' ? true : post.status === filter
  );

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h2 className="text-3xl font-cinzel text-gold mb-4">Content Calendar</h2>
        <div className="flex gap-4">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-full ${
              filter === 'all' 
                ? 'bg-gold text-black' 
                : 'bg-gray-800 text-white hover:bg-gray-700'
            }`}
          >
            All Posts
          </button>
          <button
            onClick={() => setFilter('published')}
            className={`px-4 py-2 rounded-full ${
              filter === 'published' 
                ? 'bg-gold text-black' 
                : 'bg-gray-800 text-white hover:bg-gray-700'
            }`}
          >
            Published
          </button>
          <button
            onClick={() => setFilter('scheduled')}
            className={`px-4 py-2 rounded-full ${
              filter === 'scheduled' 
                ? 'bg-gold text-black' 
                : 'bg-gray-800 text-white hover:bg-gray-700'
            }`}
          >
            Scheduled
          </button>
          <button
            onClick={() => setFilter('draft')}
            className={`px-4 py-2 rounded-full ${
              filter === 'draft' 
                ? 'bg-gold text-black' 
                : 'bg-gray-800 text-white hover:bg-gray-700'
            }`}
          >
            Drafts
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPosts.map((post) => (
          <Card key={post.slug} className="bg-gray-900 border-gray-800">
            <CardHeader>
              <div className="flex justify-between items-start">
                <CardTitle className="text-xl font-cinzel text-gold">
                  <Link to={`/blog/${post.slug}`} className="hover:text-gold/80">
                    {post.title}
                  </Link>
                </CardTitle>
                <span className={`px-2 py-1 rounded-full text-xs ${
                  post.status === 'published' 
                    ? 'bg-green-900 text-green-300'
                    : post.status === 'scheduled'
                    ? 'bg-blue-900 text-blue-300'
                    : 'bg-gray-800 text-gray-300'
                }`}>
                  {post.status.charAt(0).toUpperCase() + post.status.slice(1)}
                </span>
              </div>
              <CardDescription className="text-white/60">
                {new Date(post.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-white/80 mb-4">{post.excerpt}</p>
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-1 bg-gray-800 rounded-full text-xs text-gold"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <div className="mt-4 text-sm text-white/60">
                By {post.author} • {post.authorRole}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default BlogCalendar; 